import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useParams, useLocation, Link } from 'react-router-dom';
import {
  useEntityDoc, useEntityList, useMyOrgRole, useOrgProjects, useFavoriteProjects,
  useMyWorkspaceRoles, TenantContext, isPrivileged, roleSatisfies, setCurrentWorkspace,
} from '../../data/entities';
import CopilotPanel from '../../components/CopilotPanel';
import { AppFocusProvider, AppFocusSwitcher } from '../../components/AppSelector';
import { MODULES, SECTIONS } from '../../data/consoleModules';

// Platform shell for everything inside an organization. The sidebar is the
// Builder deck's module order (decision deck-ia-console) folded into four
// spine sections — Build · Ship · Live · Organization (decision
// sidebar-spine-sections): Home and Solutions on top, then the sections,
// then Projects (favorites pinned); with a project selected the
// workspace-scoped views (nQube / CloudOps / Dev Studio / Listen) show that
// project's data. The sidebar is the ONLY navigation that moves between
// modules (decision ux-single-nav-in-org): the org switcher, search and
// notifications live in the top strip (PlatformNav / Navbar → OrgStrip),
// and under 700px this sidebar is a drawer that strip's burger opens (between
// 700 and 900px — foldables open, small tablets — it stays as a 200px rail).
const NON_FAV_LIMIT = 4;

// Section open state is remembered per org; Build starts open, the rest
// closed. The section holding the active module is always open (its header
// cannot collapse it) so the current page is never hidden from the sidebar.
const SECTIONS_KEY = (orgId) => `nv.side.sections.${orgId}`;
const DEFAULT_OPEN = { build: true };
function readOpen(orgId) {
  try { return { ...DEFAULT_OPEN, ...JSON.parse(localStorage.getItem(SECTIONS_KEY(orgId)) || '{}') }; } catch { return { ...DEFAULT_OPEN }; }
}

function SideSection({ section, open, active, onToggle, children }) {
  const isOpen = open || active;
  return (
    <div className={`pf-side-section${isOpen ? ' is-open' : ''}${active ? ' is-active' : ''}`}>
      <button
        type="button"
        className="pf-side-section-head"
        aria-expanded={isOpen}
        aria-controls={`side-${section.id}`}
        title={active ? `${section.label} — holds the current page` : section.tag}
        onClick={active ? undefined : onToggle}
      >
        <span className="pf-side-section-label">{section.label}</span>
        <span className="pf-side-section-tag">{section.tag}</span>
        <span className="pf-side-section-chev" aria-hidden="true">▾</span>
      </button>
      {/* Always in the DOM: a closed body is hidden via CSS on every width —
          desktop, the 700–900px compact rail, and the <700px drawer. */}
      <div className="pf-side-section-body" id={`side-${section.id}`}>{children}</div>
    </div>
  );
}

// Selected workspace is remembered per org (null = every workspace I can see).
const WS_KEY = (orgId) => `nv.ws.${orgId}`;
const readWs = (orgId) => { try { return localStorage.getItem(WS_KEY(orgId)) || null; } catch { return null; } };

export default function OrgShell() {
  const { orgId } = useParams();
  const location = useLocation();
  const { doc: org } = useEntityDoc('org', {}, orgId);
  const myRole = useMyOrgRole(orgId);
  const projects = useOrgProjects(orgId);
  const [favs, toggleFav] = useFavoriteProjects(orgId);
  // Workspace tenancy: the workspaces I am seated in (org admins see all),
  // and the one selected in the switcher. Field-scoped lists (apps, agents,
  // knowledge, builder apps) read this through TenantContext.
  const { docs: workspaces } = useEntityList('workspace', { org: orgId }, { orderBy: 'name', dir: 'asc' });
  const wsRoles = useMyWorkspaceRoles(orgId);
  const [workspace, setWorkspaceState] = useState(() => readWs(orgId));
  const setWorkspace = (id) => {
    setWorkspaceState(id || null);
    try { if (id) localStorage.setItem(WS_KEY(orgId), id); else localStorage.removeItem(WS_KEY(orgId)); } catch { /* ignore */ }
  };
  const privileged = isPrivileged(myRole);
  const seatedIds = (wsRoles || []).map((r) => r.workspaceId);
  const myWorkspaces = (workspaces || []).filter((w) => privileged || seatedIds.includes(w.id));
  const selected = workspace && myWorkspaces.some((w) => w.id === workspace) ? workspace : null;
  const tenant = {
    orgId,
    role: myRole || null,
    workspaceIds: seatedIds,
    workspace: selected,
    workspaces: myWorkspaces,
    wsRoles: wsRoles || [],
    setWorkspace,
  };
  // Creates made while scoped land in the selected workspace (entities.js).
  useEffect(() => { setCurrentWorkspace(selected); return () => setCurrentWorkspace(null); }, [selected]);
  const [openSections, setOpenSections] = useState(() => readOpen(orgId));
  // Narrow screens: the sidebar is a drawer the top strip's burger opens;
  // any navigation closes it.
  const [drawer, setDrawer] = useState(false);
  useEffect(() => {
    const onToggle = () => setDrawer((v) => !v);
    window.addEventListener('nv:toggle-sidebar', onToggle);
    return () => window.removeEventListener('nv:toggle-sidebar', onToggle);
  }, []);
  useEffect(() => { setDrawer(false); }, [location.pathname, location.search]);

  // Remember the org for the sign-in redirect (ConsoleHome in App.js).
  useEffect(() => {
    try { localStorage.setItem('nv.lastOrgId', orgId); } catch { /* ignore */ }
    setOpenSections(readOpen(orgId));
  }, [orgId]);

  const toggleSection = (id) => {
    setOpenSections((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(SECTIONS_KEY(orgId), JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  // Active project comes from the URL — deep links and refresh keep working.
  const m = location.pathname.match(/^\/orgs\/[^/]+\/p\/([^/]+)\/([^/]+)/);
  const active = m ? { ws: m[1], id: m[2] } : null;
  const pBase = active ? `p/${active.ws}/${active.id}` : null;

  if (org === undefined) return <div className="cs-page"><p className="cs-help">Loading…</p></div>;
  if (org === null) {
    return (
      <div className="cs-page">
        <p className="cs-help">Organization not found or you're not a member. <Link to="/orgs">Back</Link></p>
      </div>
    );
  }

  const favKey = (p) => `${p.wsId}/${p.id}`;
  const scopedProjects = (projects || []).filter((p) => !tenant.workspace || p.wsId === tenant.workspace);
  const favProjects = scopedProjects.filter((p) => favs.includes(favKey(p)));
  const otherProjects = scopedProjects.filter((p) => !favs.includes(favKey(p)));
  const shownOthers = otherProjects.slice(0, NON_FAV_LIMIT);
  const hiddenCount = otherProjects.length - shownOthers.length;

  // The current top-level segment under /orgs/:id — lights the module whose
  // own route OR legacy aliases (apps, nqube, review…) are being viewed.
  const seg = (location.pathname.replace(/^\/orgs\/[^/]+\/?/, '').split('/')[0]) || '';
  const isModuleActive = (m) => seg === m.to || (m.matches || []).includes(seg);
  // The app focus (?app= and the ?apps= compare set, decision
  // app-focus-context) must survive a sidebar click, not just the App
  // Builder's own deep links. AppFocusProvider writes the remembered focus
  // into the URL, so forwarding the params is enough.
  const focusQ = (() => {
    const cur = new URLSearchParams(location.search);
    const q = new URLSearchParams();
    for (const k of ['app', 'apps']) if (cur.get(k)) q.set(k, cur.get(k));
    const s = q.toString();
    return s ? `?${s}` : '';
  })();
  const moduleTo = (to) => `${to}${focusQ}`;

  const projectRow = (p) => {
    const key = favKey(p);
    const isFav = favs.includes(key);
    return (
      <div key={key} className="pf-proj-row">
        <NavLink to={`p/${p.wsId}/${p.id}`} className="pf-proj-link" title={`${p.name} — ${p.wsName}`}>
          <span className="pf-proj-name">{p.name}</span>
          <span className="pf-side-tag">{p.wsName}</span>
        </NavLink>
        <button
          type="button"
          className={`pf-star ${isFav ? 'is-fav' : ''}`}
          title={isFav ? 'Remove favorite' : 'Add favorite'}
          onClick={() => toggleFav(key)}
        >
          {isFav ? '★' : '☆'}
        </button>
      </div>
    );
  };

  return (
    <TenantContext.Provider value={tenant}>
    <AppFocusProvider orgId={orgId}>
    <div className={`pf-shell${drawer ? ' pf-drawer-open' : ''}`}>
      {drawer && <div className="pf-drawer-backdrop" onClick={() => setDrawer(false)} aria-hidden="true" />}
      <aside className="pf-sidebar" aria-label="Modules">
        <div className="pf-side-org">{org.name}</div>

        <NavLink to="" end className="pf-side-link">
          <span className="pf-side-label">Home</span>
          <span className="pf-side-tag">{roleSatisfies(myRole, 'admin') ? 'Usage, cost, and health' : 'Start here · build an app'}</span>
        </NavLink>
        <NavLink to="solutions" className="pf-side-link">
          <span className="pf-side-label">Solutions</span>
          <span className="pf-side-tag">Foundation, Firebase, own host</span>
        </NavLink>
        {SECTIONS.map((section) => {
          const mods = MODULES.filter((m) => m.section === section.id);
          return (
            <SideSection
              key={section.id}
              section={section}
              open={!!openSections[section.id]}
              active={mods.some(isModuleActive)}
              onToggle={() => toggleSection(section.id)}
            >
              {mods.map((mod) => (
                <NavLink
                  key={mod.to}
                  to={moduleTo(mod.to)}
                  className={() => `pf-side-link${isModuleActive(mod) ? ' active' : ''}`}
                >
                  <span className="pf-side-label">{mod.label}</span>
                  {mod.tag && <span className="pf-side-tag">{mod.tag}</span>}
                </NavLink>
              ))}
            </SideSection>
          );
        })}

        <span className="pf-side-heading" style={{ marginTop: 14 }}>Workspace</span>
        <label className="pf-side-ws" title="Workspace scope: apps, agents, knowledge and projects shown everywhere narrow to this workspace">
          <select
            className="cs-input cs-select"
            value={tenant.workspace || ''}
            onChange={(e) => setWorkspace(e.target.value || null)}
            aria-label="Workspace"
          >
            <option value="">{privileged ? 'All workspaces' : myWorkspaces.length ? 'All my workspaces' : 'No workspace seats'}</option>
            {myWorkspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </label>

        <span className="pf-side-heading" style={{ marginTop: 10 }}>App</span>
        <AppFocusSwitcher />

        <span className="pf-side-heading" style={{ marginTop: 10 }}>Projects</span>
        {projects === null && <span className="cs-help" style={{ padding: '0 10px', fontSize: 12 }}>Loading…</span>}
        {projects && !scopedProjects.length && (
          <span className="cs-help" style={{ padding: '0 10px', fontSize: 12 }}>
            {tenant.workspace ? 'None in this workspace yet.' : 'None yet — create one from a workspace.'}
          </span>
        )}
        {favProjects.map(projectRow)}
        {shownOthers.map(projectRow)}
        {hiddenCount > 0 && (
          <Link to="" className="pf-side-more">+ {hiddenCount} more — browse all</Link>
        )}
        {pBase && (
          <span className="cs-help" style={{ padding: '6px 10px 0', fontSize: 11 }}>
            Project views: <Link to={`${pBase}/nqube`}>nQube</Link> · <Link to={`${pBase}/listen`}>Listen</Link> · <Link to={`${pBase}/cloudops`}>DevOps</Link> · <Link to={`${pBase}/devstudio`}>Dev Studio</Link>
          </span>
        )}

        <div className="pf-side-foot">
          <span className="cs-help" style={{ fontSize: 11 }}>your role: {myRole || '…'}{tenant.workspace && !privileged ? ` · ${(wsRoles || []).find((r) => r.workspaceId === tenant.workspace)?.role || '—'} here` : ''}</span>
        </div>
      </aside>
      <main className="pf-main">
        <Outlet context={{ org: { id: orgId, ...org }, myRole, tenant }} />
      </main>
      <CopilotPanel orgId={orgId} />
    </div>
    </AppFocusProvider>
    </TenantContext.Provider>
  );
}
